// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// A complete, exactly-restorable world (M4-28a).
//
// M0-17 found the need for this. Validating a two-hour replay is 7.2 million steps, past what one
// Edge Function invocation may spend, so a long game has to be validated in chunks — and a chunk
// boundary is only meaningful if the simulation can be put down and picked up **bit for bit**.
//
// What existed before was `BallSnapshot`: position, velocity, spin. That is the state a renderer
// needs and nowhere near the state a simulation needs. A world restored from ball state alone
// resumes with the RNG at the wrong place in its stream, every scoop empty, every drop target
// standing, the flippers down and the frame still. It looks right for one frame and is a
// different game by the second.
//
// So this carries everything that changes:
//
//   * **the RNG stream position** — not the seed, the position, because the next number decides
//     the next kickback and a seed replays the whole stream from the beginning;
//   * **every device's own state**, via `Device.snapshot()`, which each mechanism implements for
//     itself so a scoop's held ball and its release step travel together;
//   * **both flippers' angle *and* angular velocity**, because a bat halfway through a stroke that
//     resumes stationary hits the ball at a different speed;
//   * **the nudge frame's displacement and velocity**, which is the machine still ringing;
//   * **the pending event queue**, because events emitted on the last step of a chunk are read by
//     the rule engine on the first step of the next one.
//
// Configuration is deliberately absent. A snapshot restores into a world built from the same
// definition; carrying the geometry would mean a restore could quietly reshape the machine, and a
// definition mismatch is something the validator already refuses before it gets this far.
//
// **Plain data, no classes.** A snapshot is written to a database column and read back by another
// process. Anything with behaviour in it would have to be revived, and revival is where a
// resumable simulation stops being exactly restorable.

import type { BallSnapshot } from './Ball'
import { restoreBall, snapshotBall } from './Ball'
import type { DeviceState } from './devices/Device'
import type { SimEvent } from './events'
import type { FlipperState } from './Flipper'
import type { World } from './World'

export type { FlipperState }

/** Everything about a world that changes. */
export interface WorldSnapshot {
  readonly stepIndex: number
  readonly rng: readonly [number, number, number, number]
  readonly balls: readonly BallSnapshot[]
  readonly flippers: readonly FlipperState[]
  /** In `World.devices` order, which is the order the loader built them in. */
  readonly devices: readonly DeviceState[]
  readonly frameX: number
  readonly frameY: number
  readonly frameVX: number
  readonly frameVY: number
  readonly nudgedAtStep: number
  readonly nudgeImpulseX: number
  readonly nudgeImpulseY: number
  readonly sweptClamps: number
  /** Emitted and not yet drained. Small — a busy multiball produces about fifty a second. */
  readonly events: readonly SimEvent[]
}

export function snapshotWorld(world: World): WorldSnapshot {
  return {
    stepIndex: world.stepIndex,
    rng: world.rng.saveState(),
    balls: world.balls.map(snapshotBall),
    flippers: world.flippers.map((f) => f.snapshot()),
    devices: world.devices.map((d) => d.snapshot()),
    frameX: world.frameX,
    frameY: world.frameY,
    frameVX: world.frameVX,
    frameVY: world.frameVY,
    nudgedAtStep: world.nudgedAtStep,
    nudgeImpulseX: world.nudgeImpulseX,
    nudgeImpulseY: world.nudgeImpulseY,
    sweptClamps: world.sweptClamps,
    events: world.events.peek().map((e) => ({ ...e })),
  }
}

/**
 * Put a world back exactly as it was.
 *
 * The world must have been built from the same definition — same devices, same order, same ball
 * count. That is checked rather than assumed: a silent partial restore is the worst possible
 * outcome here, because it produces a simulation that runs perfectly and is not the one that was
 * saved, and nothing downstream can tell.
 */
export function restoreWorld(world: World, snapshot: WorldSnapshot): void {
  if (world.balls.length !== snapshot.balls.length) {
    throw new Error(
      `restoreWorld: ${snapshot.balls.length} balls in the snapshot, ${world.balls.length} in the world`,
    )
  }
  if (world.devices.length !== snapshot.devices.length) {
    throw new Error(
      `restoreWorld: ${snapshot.devices.length} devices in the snapshot, ${world.devices.length} in the world`,
    )
  }
  if (world.flippers.length !== snapshot.flippers.length) {
    throw new Error(
      `restoreWorld: ${snapshot.flippers.length} flippers in the snapshot, ${world.flippers.length} in the world`,
    )
  }

  world.stepIndex = snapshot.stepIndex
  world.rng.loadState(snapshot.rng)
  for (const [i, ball] of world.balls.entries()) restoreBall(ball, snapshot.balls[i]!)
  for (const [i, flipper] of world.flippers.entries()) flipper.restore(snapshot.flippers[i]!)
  for (const [i, device] of world.devices.entries()) device.restore(snapshot.devices[i]!)

  world.frameX = snapshot.frameX
  world.frameY = snapshot.frameY
  world.frameVX = snapshot.frameVX
  world.frameVY = snapshot.frameVY
  world.nudgedAtStep = snapshot.nudgedAtStep
  world.nudgeImpulseX = snapshot.nudgeImpulseX
  world.nudgeImpulseY = snapshot.nudgeImpulseY
  world.sweptClamps = snapshot.sweptClamps

  world.events.clear()
  for (const e of snapshot.events) world.events.emit(e.type, e.device, e.ball, e.step, e.value)
}
